import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Tag,
  Avatar,
  Space,
  Button,
  Divider,
  List,
  Input,
  Form,
  message,
  Skeleton,
  Row,
  Col,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  EyeOutlined,
  LikeOutlined,
  MessageOutlined,
  UserOutlined,
  HeartOutlined,
  HeartFilled,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { mockPosts, mockUsers } from '../../api/mockData';
import './index.css';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [comments, setComments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    // 模拟API调用
    setTimeout(() => {
      const foundPost = mockPosts.find(p => p.id === id);
      if (foundPost) {
        setPost(foundPost);
        setLikes(foundPost.likes);
        // 模拟评论数据
        setComments([
          {
            id: '1',
            author: '用户A',
            avatar: 'https://picsum.photos/50/50?random=10',
            content: '这篇文章很有深度，学到了很多！',
            datetime: '2023-12-15 10:30',
            replies: [
              {
                id: '1-1',
                author: '作者',
                avatar: 'https://picsum.photos/50/50?random=11',
                content: '谢谢支持！',
                datetime: '2023-12-15 11:00'
              }
            ]
          },
          {
            id: '2',
            author: '用户B',
            avatar: 'https://picsum.photos/50/50?random=12',
            content: '期待后续的系列文章！',
            datetime: '2023-12-15 14:20'
          }
        ]);
      }
      setLoading(false);
    }, 800);
  }, [id]);

  const handleLike = () => {
    if (liked) {
      setLikes(likes - 1);
      message.success('取消点赞');
    } else {
      setLikes(likes + 1);
      message.success('点赞成功');
    }
    setLiked(!liked);
  };

  const handleCommentSubmit = () => {
    if (!commentValue.trim()) {
      message.warning('请输入评论内容');
      return;
    }

    setSubmitting(true);
    // 模拟提交
    setTimeout(() => {
      const newComment = {
        id: Date.now().toString(),
        author: '当前用户',
        avatar: 'https://picsum.photos/50/50?random=13',
        content: commentValue,
        datetime: '刚刚'
      };
      
      setComments([newComment, ...comments]);
      setCommentValue('');
      form.resetFields();
      setSubmitting(false);
      message.success('评论发表成功');
    }, 500);
  };

  const handleEdit = () => {
    navigate(`/edit/${id}`);
  };

  const handleDelete = () => {
    message.info('文章已删除');
    navigate('/');
  };

  const handleShare = () => {
    message.success('链接已复制到剪贴板');
    // 实际应用中这里应该复制文章链接
  };

  if (loading) {
    return (
      <div className="container post-detail-loading">
        <Card>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container post-not-found">
        <Card>
          <Title level={2}>文章不存在</Title>
          <Button type="primary" onClick={() => navigate('/')}>
            返回首页
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      <div className="container">
        {/* 返回按钮 */}
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-button"
        >
          返回列表
        </Button>

        {/* 文章内容 */}
        <Card className="post-content-card">
          <div className="post-header">
            <Title level={2}>{post.title}</Title>
            
            <div className="post-meta">
              <Space size="large" wrap>
                <span>
                  <Avatar src={mockUsers[0]?.avatar} size="small" />
                  <span className="author-name">{post.author}</span>
                </span>
                <span>
                  <CalendarOutlined /> {post.date}
                </span>
                <span>
                  <EyeOutlined /> {post.views}
                </span>
                <span>
                  <Tag color="blue">{post.category}</Tag>
                </span>
              </Space>
            </div>

            <div className="post-tags">
              {post.tags.map(tag => (
                <Tag key={tag} color="geekblue">{tag}</Tag>
              ))}
            </div>
          </div>

          <Divider />

          {/* 文章封面图 */}
          {post.coverImage && (
            <div className="post-cover">
              <img src={post.coverImage} alt={post.title} />
            </div>
          )}

          {/* 文章正文 */}
          <div className="post-body">
            <ReactMarkdown className="markdown-content">
              {post.content}
            </ReactMarkdown>
          </div>

          {/* 操作按钮 */}
          <Divider />
          <div className="post-actions">
            <Space size="large">
              <Button
                type={liked ? 'primary' : 'default'}
                icon={liked ? <HeartFilled /> : <HeartOutlined />}
                onClick={handleLike}
              >
                喜欢 ({likes})
              </Button>
              <Button icon={<MessageOutlined />}>
                评论 ({comments.length})
              </Button>
              <Button icon={<ShareAltOutlined />} onClick={handleShare}>
                分享
              </Button>
              <Button icon={<EditOutlined />} onClick={handleEdit}>
                编辑
              </Button>
              <Button 
                icon={<DeleteOutlined />} 
                danger
                onClick={handleDelete}
              >
                删除
              </Button>
            </Space>
          </div>
        </Card>

        {/* 评论区域 */}
        <Card title={`评论 (${comments.length})`} className="comments-card">
          {/* 评论输入框 */}
          <Form form={form}>
            <Form.Item>
              <TextArea
                rows={4}
                value={commentValue}
                onChange={(e) => setCommentValue(e.target.value)}
                placeholder="写下你的评论..."
              />
            </Form.Item>
            <Form.Item>
              <Button
                htmlType="submit"
                loading={submitting}
                onClick={handleCommentSubmit}
                type="primary"
              >
                发表评论
              </Button>
            </Form.Item>
          </Form>

          {/* 评论列表 */}
          <List
  className="comment-list"
  itemLayout="horizontal"
  dataSource={comments}
  renderItem={item => (
    <List.Item>
      <List.Item.Meta
        avatar={<Avatar src={item.avatar} />}
        title={
          <div>
            <span style={{ marginRight: 8 }}>{item.author}</span>
            <span style={{ fontSize: 12, color: '#999' }}>{item.datetime}</span>
          </div>
        }
        description={item.content}
      />
      {/* 如果有回复 */}
      {item.replies && item.replies.length > 0 && (
        <List
          dataSource={item.replies}
          renderItem={reply => (
            <List.Item style={{ marginLeft: 40 }}>
              <List.Item.Meta
                avatar={<Avatar size="small" src={reply.avatar} />}
                title={
                  <div>
                    <span style={{ marginRight: 8 }}>{reply.author}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>{reply.datetime}</span>
                  </div>
                }
                description={reply.content}
              />
            </List.Item>
          )}
        />
      )}
    </List.Item>
  )}
/>
        </Card>

        {/* 相关文章 */}
        <Card title="相关文章" className="related-posts-card">
          <Row gutter={[16, 16]}>
            {mockPosts
              .filter(p => p.id !== id && p.category === post.category)
              .slice(0, 3)
              .map(relatedPost => (
                <Col xs={24} md={8} key={relatedPost.id}>
                  <Card
                    hoverable
                    onClick={() => navigate(`/post/${relatedPost.id}`)}
                    className="related-post-card"
                    cover={<img alt={relatedPost.title} src={relatedPost.coverImage} />}
                  >
                    <Card.Meta
                      title={relatedPost.title}
                      description={
                        <Space>
                          <span>{relatedPost.date}</span>
                          <span>·</span>
                          <span>{relatedPost.views} 阅读</span>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              ))}
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default PostDetail;